import { Component, ViewEncapsulation, computed, input, output } from '@angular/core';

import { StoreLogo } from '../store-logo/store-logo';
import { CouponCardView } from './coupon-card.models';

/**
 * How the card is being used:
 * - `default`  — a row in a coupon list; the exclusive outline follows the coupon's own flag
 * - `featured` — the store page's lead offer, which always carries the exclusive outline and is the
 *                only place the "Top Pick" badge appears
 * - `expired`  — greyed out and labelled Unverified, but still clickable (expired codes often work)
 */
export type CouponCardVariant = 'default' | 'featured' | 'expired';

/** See `descriptionIsClamped` — the length past which the two-line clamp starts hiding copy. */
const CLAMP_CHARS = 140;

/**
 * The site's coupon card, shared by the store page and the store discount pages.
 *
 * It is purely presentational: it renders a `CouponCardView` and emits the click. What happens next
 * — opening the `/go/` redirect, showing the offer modal, recording a hit — belongs to the
 * page, because that differs per surface.
 *
 * `ViewEncapsulation.None` is deliberate. The styles were lifted verbatim out of store-page.scss so
 * the two surfaces cannot drift, and they are already namespaced under `.sav-store-offer-card` /
 * `.sav-offer-*`, which nothing else on the site uses.
 */
@Component({
  selector: 'app-coupon-card',
  imports: [StoreLogo],
  templateUrl: './coupon-card.html',
  styleUrl: './coupon-card.scss',
  encapsulation: ViewEncapsulation.None,
})
export class CouponCard {
  readonly card = input.required<CouponCardView>();
  readonly variant = input<CouponCardVariant>('default');
  /** Emitted for both "Show Code" and "Get Deal" — the page decides what that means. */
  readonly action = output<CouponCardView>();

  protected readonly isExpired = computed(() => this.variant() === 'expired');

  protected readonly showExclusiveStyle = computed(
    () => !this.isExpired() && (this.variant() === 'featured' || this.card().isExclusive),
  );

  /**
   * An offer that belongs to a DIFFERENT brand than the page it sits on — the admin attached an
   * affiliate store to it. On a store page these sit among the store's own coupons, so they get an
   * outline and carry the other brand's name in their badge rather than a generic "Offer".
   */
  protected readonly isAffiliateOffer = computed(() =>
    Boolean(this.card().affiliateStoreName || this.card().affiliateStoreLogoUrl),
  );

  /** The badge above the title: the partner brand's name when there is one, else the offer type. */
  protected readonly typeBadgeLabel = computed(() => {
    const card = this.card();
    return this.isAffiliateOffer() ? card.affiliateStoreName || card.typeLabel : card.typeLabel;
  });

  /**
   * The page computes a font size to fit the *type* label; a store name is different text, so that
   * override must not follow it or a long brand name overflows the pill.
   */
  protected readonly typeBadgeFontSize = computed(() =>
    this.isAffiliateOffer() ? null : this.card().typeLabelFontSize,
  );

  /**
   * The offer type rendered inline with the discount on phones ("50% OFF DEAL").
   *
   * Empty when the card has no text discount to join — an affiliate logo or a coupon image fills
   * that block instead — and empty when the label would only repeat a word the discount already
   * shows. A legacy feed deal with no badges reads "DEAL" in the discount and "Deal" as its type,
   * and "DEAL DEAL" tells the reader nothing twice.
   */
  protected readonly inlineTypeLabel = computed(() => {
    const card = this.card();
    if (this.isAffiliateOffer() || card.imageUrl) return '';
    const label = card.typeLabel.trim();
    const shown = [card.offerPrimary, card.offerSecondary].map((s) => s.trim().toLowerCase());
    return shown.includes(label.toLowerCase()) ? '' : label;
  });

  /** Distinct per card, so one "Read more" cannot toggle another card's description. */
  protected readonly descriptionToggleId = computed(() => `sav-offer-desc-${this.card().id}`);

  /**
   * Whether to render the expand control at all.
   *
   * CSS cannot measure its own overflow, so the decision is made here from the length of the copy
   * and rendered into the HTML — which means it is correct in SSR output, before any client code
   * runs. `CLAMP_CHARS` is roughly what fits in the two visible lines at the narrowest width the
   * middle column still spans; below that the description is fully visible inside the clamp and a
   * toggle would expand nothing.
   *
   * The description is always printed in full either way. The clamp is purely visual, so this
   * changes what a reader can click, never what a crawler reads.
   */
  protected readonly descriptionIsClamped = computed(
    () => (this.card().description ?? '').length > CLAMP_CHARS,
  );

  protected onAction(): void {
    this.action.emit(this.card());
  }
}
